import {
  Box,
  Button,
  Modal,
  Dropdown,
  DropdownItem,
  Typography,
  getColor,
  CalendarRange,
} from "fds";
import i18n from "i18next";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import utilsCommon from "../../shared/utils/common";
import dayjs from "dayjs";
import useAdminHomeStore from "../../redux/dispatcher/useAdminHomeStore";

const HomeBody = () => {
  const { t } = useTranslation();

  const { adminHomeStore, getLineChart, downloadChart } = useAdminHomeStore();

  const [dateValue, setDateValue] = useState("OneMonth");
  const [showDatePickerModal, toggleDatePickerModal] = useState(false);
  const [selectedTempDate, changeSelectedTempDate] = useState({
    start: utilsCommon.getStartDay(new Date()),
    end: utilsCommon.getEndDay(new Date()),
  });
  const [selectedDate, changeSelectedDate] = useState({
    startDate: utilsCommon.getDateTimeNum(
      dayjs(
        utilsCommon.getDateStr(utilsCommon.getStartPeriod("1month"))
      ).format("YYYY-MM-DD")
    ),
    endDate: dayjs().valueOf(),
  });
  const [chartType, setChartType] = useState("chart_createWorkgroup");
  const [newData, setNewData] = useState<any[]>([]);
  const [highChartOption, setHighChartOption] = useState<Highcharts.Options>();
  const [buttonClick, setButtonClick] = useState(-1);

  useEffect(() => {
    getLineChart(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    setLineChartState(adminHomeStore.linechart);
  }, [adminHomeStore]);

  const [lineChartState, setLineChartState] = useState({
    docStatsMonthly: [] as {
      logDate: string;
      count: number;
    }[],
    memberStatsMonthly: [] as {
      logDate: string;
      count: number;
    }[],
    workgroupStatsMonthly: [] as {
      logDate: string;
      count: number;
    }[],
  });

  useEffect(() => {
    let newDate = [] as any;
    let startDate = "";
    if (
      lineChartState.workgroupStatsMonthly.length !== 0 ||
      lineChartState.memberStatsMonthly.length !== 0 ||
      lineChartState.docStatsMonthly.length !== 0
    ) {
      if (chartType === "chart_createWorkgroup") {
        const sortedData = [...lineChartState.workgroupStatsMonthly].sort(
          (a, b) => a.logDate.localeCompare(b.logDate)
        );
        sortedData.map((item, index) => {
          if (index === 0) startDate = dayjs(item.logDate).format("YYYY-MM-DD");
          newDate.push([
            dayjs(item.logDate).format(utilsCommon.getDateFormat()),
            item.count,
          ]);
        });
      } else if (chartType === "chart_newUser") {
        const sortedData = [...lineChartState.memberStatsMonthly].sort((a, b) =>
          a.logDate.localeCompare(b.logDate)
        );
        sortedData.map((item, index) => {
          if (index === 0) startDate = dayjs(item.logDate).format("YYYY-MM-DD");
          newDate.push([
            dayjs(item.logDate).format(utilsCommon.getDateFormat()),
            item.count,
          ]);
        });
      } else if (chartType === "chart_sharedFile") {
        const sortedData = [...lineChartState.docStatsMonthly].sort((a, b) =>
          a.logDate.localeCompare(b.logDate)
        );
        sortedData.map((item, index) => {
          if (index === 0) startDate = dayjs(item.logDate).format("YYYY-MM-DD");
          newDate.push([
            dayjs(item.logDate).format(utilsCommon.getDateFormat()),
            item.count,
          ]);
        });
      }
    }
    setNewData(newDate);
  }, [chartType, dateValue, lineChartState]);

  useEffect(() => {
    setHighChartOption({
      chart: {
        type: "line",
        height: 270,
      },
      credits: {
        text: "",
        href: "",
        enabled: false,
      },
      title: {
        text: "",
      },
      xAxis: {
        type: "datetime",
        categories: newData.map((item) => item[0]),
        dateTimeLabelFormats: {
          year: i18n.language === "ko" ? "%Y" : "%Y",
          month: i18n.language === "ko" ? "%Y/%m" : "%b %Y",
          week: i18n.language === "ko" ? "%m/%d" : "%b %e",
          day: i18n.language === "ko" ? "%m/%d" : "%b %e",
          hour: " ",
          minute: " ",
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: "",
        },
      },
      legend: {
        enabled: false,
        reversed: true,
      },
      tooltip: {
        enabled: true,
        headerFormat: "",
        pointFormat: "<b>{point.name}</b><br/>Total: {point.y:,.0f}",
      },
      plotOptions: {
        area: {
          fillColor: {
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1,
            },
            stops: [
              [0, getColor("COLOR_ICON_BRAND")],
              [1, "#ffffff"],
            ],
          },
          marker: {
            fillColor: "#ffffff",
            lineWidth: 2,
            lineColor: getColor("COLOR_ICON_BRAND"),
          },
          lineWidth: 2,
          threshold: null,
        },
        series: {
          stacking: "normal",
        },
      },
      series: [
        {
          type: "area",
          visible: true,
          data: newData,
          color: getColor("COLOR_ICON_BRAND"),
        },
      ],
    });
  }, [newData]);

  const onChangeDate = (type: string): void => {
    let startDate: string = "";
    let endDate: number = dayjs().valueOf();
    if (type === "OneMonth") {
      setDateValue("OneMonth");
      startDate = dayjs(
        utilsCommon.getDateStr(utilsCommon.getStartPeriod("1month"))
      ).format("YYYY-MM-DD");
      changeSelectedDate({
        startDate: utilsCommon.getDateTimeNum(startDate),
        endDate: endDate,
      });
    } else if (type === "ThreeMonth") {
      setDateValue("ThreeMonth");
      startDate = dayjs(
        utilsCommon.getDateStr(utilsCommon.getStartPeriod("3months"))
      ).format("YYYY-MM-DD");
      changeSelectedDate({
        startDate: utilsCommon.getDateTimeNum(startDate),
        endDate: endDate,
      });
    } else if (type === "SixMonth") {
      setDateValue("SixMonth");
      startDate = dayjs(
        utilsCommon.getDateStr(utilsCommon.getStartPeriod("6months"))
      ).format("YYYY-MM-DD");
      changeSelectedDate({
        startDate: utilsCommon.getDateTimeNum(startDate),
        endDate: endDate,
      });
    } else if (type === "OneYear") {
      setDateValue("OneYear");
      startDate = dayjs(
        utilsCommon.getDateStr(utilsCommon.getStartPeriod("1year"))
      ).format("YYYY-MM-DD");
      changeSelectedDate({
        startDate: utilsCommon.getDateTimeNum(startDate),
        endDate: endDate,
      });
    } else if (type === "InputDate") {
      setDateValue("InputDate");
      toggleDatePickerModal(false);
    }
  };

  const handleChangeSelectedDate = () => {
    changeSelectedDate({
      startDate: dayjs(
        utilsCommon.getStartDay(new Date(selectedTempDate.start))
      ).valueOf(),
      endDate: utilsCommon.getEndDay(new Date(selectedTempDate.end)).valueOf(),
    });
    toggleDatePickerModal(false);
    onChangeDate("InputDate");
  };

  /** dateCurrentPickerModal: 기간 설정 필터 모달 */
  const datePickerModal = () => {
    return (
      <Modal
        title={t("admin.common.filter.setPeriod")}
        open={showDatePickerModal}
        onClose={() => {
          toggleDatePickerModal(false);
        }}
        footer={null}
        width={544}
        body={
          <Box direction="column" width={"100%"}>
            <Box width={"100%"}>
              <Box width={"240px"} direction={"column"}>
                <CalendarRange
                  width={240}
                  startDate={selectedTempDate?.start}
                  endDate={selectedTempDate?.end}
                  onSubmit={(date: any) => {
                    changeSelectedTempDate({
                      start: date.start,
                      end: date.end,
                    });
                  }}
                  maxDate={new Date()}
                />
              </Box>
            </Box>
            <Typography
              type="body3_rg"
              exactColor={getColor("COLOR_TEXT_BRAND")}
              classes="mg mt-10"
            >
              {t("admin.common.filter.selectPeriod")}
            </Typography>
            <Box justifyContent={"flex-end"} classes={"mg mt-32"}>
              <Button
                label={t("admin.common.button.cancel")}
                btnStyle="fill"
                type="secondary"
                onClick={() => {
                  toggleDatePickerModal(false);
                }}
              />
              <Button
                label={t("admin.common.button.ok")}
                btnStyle="fill"
                type="primary"
                classes="mg ml-8"
                onClick={() => {
                  handleChangeSelectedDate();
                }}
              />
            </Box>
          </Box>
        }
      ></Modal>
    );
  };

  return (
    <Box classes="adminHome_body">
      <Box direction="row" justifyContent="space-between">
        <Box>
          <Dropdown
            btnStyle="line"
            buttonWidth={180}
            align={"left"}
            classes="adminHome_body-select"
            placeholder={
              chartType === "chart_createWorkgroup"
                ? t("admin.home.createdWorkgroup")
                : chartType === "chart_newUser"
                ? t("admin.home.newUser")
                : chartType === "chart_sharedFile"
                ? t("admin.home.sharedFile")
                : ""
            }
            value={
              chartType === "chart_createWorkgroup"
                ? t("admin.home.createdWorkgroup")
                : chartType === "chart_newUser"
                ? t("admin.home.newUser")
                : chartType === "chart_sharedFile"
                ? t("admin.home.sharedFile")
                : ""
            }
            buttonClick={buttonClick}
          >
            <DropdownItem
              label={t("admin.home.createdWorkgroup")}
              onClick={() => {
                setChartType("chart_createWorkgroup");
                setButtonClick(buttonClick + 1);
              }}
            />
            <DropdownItem
              label={t("admin.home.newUser")}
              onClick={() => {
                setChartType("chart_newUser");
                setButtonClick(buttonClick + 1);
              }}
            />
            <DropdownItem
              label={t("admin.home.sharedFile")}
              onClick={() => {
                setChartType("chart_sharedFile");
                setButtonClick(buttonClick + 1);
              }}
            />
          </Dropdown>
        </Box>
        <Box>
          <Button
            type="primary"
            btnStyle="line"
            state={dateValue === "OneMonth" ? "select" : "default"}
            onClick={() => {
              onChangeDate("OneMonth");
            }}
            label={t("admin.common.filter.1month")}
            classes={"mg ml-8"}
          />
          <Button
            type="primary"
            btnStyle="line"
            state={dateValue === "ThreeMonth" ? "select" : "default"}
            onClick={() => {
              onChangeDate("ThreeMonth");
            }}
            label={t("admin.common.filter.3months")}
            classes={"mg ml-8"}
          />
          <Button
            type="primary"
            btnStyle="line"
            state={dateValue === "SixMonth" ? "select" : "default"}
            onClick={() => {
              onChangeDate("SixMonth");
            }}
            label={t("admin.common.filter.6months")}
            classes={"mg ml-8"}
          />
          <Button
            type="primary"
            btnStyle="line"
            state={dateValue === "OneYear" ? "select" : "default"}
            onClick={() => {
              onChangeDate("OneYear");
            }}
            label={t("admin.common.filter.1year")}
            classes={"mg ml-8"}
          />
          <Button
            type="primary"
            btnStyle="line"
            state={dateValue === "InputDate" ? "select" : "default"}
            onClick={() => {
              toggleDatePickerModal(true);
            }}
            label={
              dateValue === "InputDate"
                ? t("admin.common.filter.setPeriod") +
                  " " +
                  utilsCommon.getDateStr(
                    Number(dayjs(selectedTempDate.start))
                  ) +
                  " ~ " +
                  utilsCommon.getDateStr(Number(dayjs(selectedTempDate.end)))
                : t("admin.common.filter.setPeriod")
            }
            classes={"mg ml-8"}
          />
          <Button
            type="primary"
            btnStyle="fill"
            label={t("admin.home.downloadChart")}
            onClick={() => {
              downloadChart(selectedDate);
            }}
            classes={"mg ml-8"}
          />
        </Box>
      </Box>

      <HighchartsReact
        containerProps={{ className: "adminHome_body-chart" }}
        highcharts={Highcharts}
        options={highChartOption}
      />
      {showDatePickerModal && datePickerModal()}
    </Box>
  );
};
export default HomeBody;
